<template>
  <div class="ai-check-container">
    <!-- Header Banner -->
    <div class="ai-header-banner">
      <div class="banner-overlay"></div>
      <div class="banner-content">
        <div class="banner-left">
          <div class="ai-icon-pulse">
            <i class="el-icon-bank-card"></i>
          </div>
          <div>
            <h1 class="banner-title">QUẢN LÝ THẺ NHẬN DIỆN AI</h1>
            <p class="banner-subtitle">Quản lý danh sách thẻ từ, trạng thái kích hoạt, gán trường học và thực hiện tác vụ hàng loạt.</p>
          </div>
        </div>
        <div class="banner-actions flex gap-2">
          <el-button type="success" class="gradient-btn green-btn" icon="el-icon-plus" @click="handleAddCard">
            Thêm thẻ mới
          </el-button>
          
          <el-dropdown @command="handleCommand" class="modern-dropdown-wrapper">
            <el-button type="warning" class="gradient-btn purple-btn">
              Tác vụ hàng loạt <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown" class="modern-dropdown-menu">
              <el-dropdown-item command="activeCard" icon="el-icon-circle-check">Kích hoạt thẻ đã chọn</el-dropdown-item>
              <el-dropdown-item command="disableCard" icon="el-icon-circle-close">Hủy kích hoạt đã chọn</el-dropdown-item>
              <el-dropdown-item command="deleteCards" icon="el-icon-delete">Xóa thẻ đã chọn</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- Quick Stats Grid -->
    <div class="ai-stats-grid">
      <div class="stat-card blue">
        <div class="stat-icon"><i class="el-icon-folder-opened"></i></div>
        <div class="stat-info">
          <p class="stat-label">Tổng số thẻ</p>
          <h3 class="stat-value">{{ totalRecords }}</h3>
        </div>
      </div>
      <div class="stat-card green">
        <div class="stat-icon"><i class="el-icon-circle-check"></i></div>
        <div class="stat-info">
          <p class="stat-label">Thẻ đang hoạt động (trang này)</p>
          <h3 class="stat-value">{{ stats.activeCount }}</h3>
        </div>
      </div>
      <div class="stat-card purple">
        <div class="stat-icon"><i class="el-icon-circle-close"></i></div>
        <div class="stat-info">
          <p class="stat-label">Thẻ chưa gán sử dụng (trang này)</p>
          <h3 class="stat-value">{{ stats.unusedCount }}</h3>
        </div>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="glass-card mb-4">
      <el-form :inline="true" :model="dataFilter" class="modern-filter-form">
        <el-form-item label="Trường học">
          <el-select
            v-model="dataFilter.idSchool"
            filterable
            clearable
            placeholder="Tất cả các trường"
            class="modern-select"
            @change="searchInformationCard"
          >
            <el-option
              v-for="item in schoolList"
              :key="item.id"
              :value="item.id"
              :label="`${item.id} - ${item.schoolName}`"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Trạng thái sử dụng">
          <el-select
            v-model="dataFilter.usageStatus"
            clearable
            placeholder="Tất cả trạng thái"
            class="modern-select"
            @change="searchInformationCard"
          >
            <el-option
              v-for="item in listUsageStatus"
              :key="item.id"
              :value="item.value"
              :label="item.name"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Số thẻ">
          <el-input
            v-model="dataFilter.cardNumber"
            clearable
            placeholder="Nhập số thẻ..."
            class="modern-input"
            prefix-icon="el-icon-search"
            @keyup.enter.native="searchInformationCard"
            @clear="searchInformationCard"
          />
        </el-form-item>

        <el-form-item class="filter-actions">
          <el-button type="primary" icon="el-icon-search" @click="searchInformationCard">Tìm kiếm</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- Main Table -->
    <div class="glass-card table-container">
      <el-table
        ref="multipleTable"
        :data="dataTable"
        v-loading="loading.table"
        element-loading-text="Đang tải danh sách thẻ nhận diện..."
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(255,255,255,0.7)"
        class="modern-table"
        border
        highlight-current-row
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" align="center" />
        
        <el-table-column type="index" :index="calculateStt" label="STT" width="55" align="center" />

        <el-table-column label="Số thẻ" min-width="200" align="center">
          <template slot-scope="{ row }">
            <span class="card-number-badge">
              <i class="el-icon-bank-card"></i>
              {{ row.cardNumber }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Trạng thái sử dụng" min-width="180" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="getUsageStatusTagType(row.usageStatus)" size="medium" effect="dark" class="status-tag-rect">
              {{ getUsageStatusLabel(row.usageStatus) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Tên trường học" min-width="300" align="left">
          <template slot-scope="{ row }">
            <span class="school-name-text">
              <i class="el-icon-school" style="color: #64748b; margin-right: 6px;"></i>
              {{ row.schoolName || '—' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Tác vụ" width="340" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              size="mini"
              type="success"
              icon="el-icon-check"
              :disabled="row.usageStatus !== 'delete'"
              @click="activeCard(row)"
            >Kích hoạt
            </el-button>
            
            <el-button
              size="mini"
              type="warning"
              icon="el-icon-close"
              :disabled="row.usageStatus === 'delete'"
              @click="deActiveCard(row)"
            >Hủy kích hoạt
            </el-button>
            
            <el-button
              size="mini"
              type="danger"
              icon="el-icon-delete"
              @click="deleteCard(row)"
            >Xóa
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination controls -->
      <div class="pagination-wrapper">
        <el-pagination
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentPageChange"
          :current-page.sync="dataFilter.page"
          :page-sizes="pageListDefaultCardOneAdmin"
          :page-size="dataFilter.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalRecords"
        >
        </el-pagination>
      </div>
    </div>

    <!-- Add Card Dialog -->
    <AddCardDialog
      :is-open="isOpen.add"
      :schoolList="schoolList"
      @closeDialog="handleCloseDialogAdd"
    />
  </div>
</template>

<script>
import SystemConfigService from "@/services/AdminService/SystemConfigService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import cardManagerService from "@/services/ManagerRecognition/CardManagerService";
import AddCardDialog from "@/views/master/identify/dialog/AddCardDialog.vue";

export default {
  name: "CardManager",
  components: { AddCardDialog },
  data() {
    return {
      dataTable: [],
      dataFilter: {
        idSchool: "",
        cardNumber: "",
        usageStatus: "",
        page: 1,
        limit: 50
      },
      listUsageStatus: [
        { id: 1, name: "Đã sử dụng", value: "use" },
        { id: 2, name: "Chưa sử dụng", value: "not use" },
        { id: 3, name: "Hủy kích hoạt", value: "delete" }
      ],
      schoolList: [],
      loading: {
        table: false
      },
      totalRecords: 0,
      isOpen: {
        add: false
      },
      listCard: []
    };
  },

  computed: {
    pageListDefaultCardOneAdmin() {
      return this.$pageListDefaultCardOneAdmin || [10, 25, 50, 100];
    },

    stats() {
      let activeCount = 0;
      let unusedCount = 0;
      this.dataTable.forEach(x => {
        if (x.usageStatus === "use") activeCount++;
        if (x.usageStatus === "not use") unusedCount++;
      });
      return { activeCount, unusedCount };
    }
  },

  created() {
    this.findAllAppIconForSchool();
    this.findAllCardManager();
  },

  methods: {
    handleCommand(command) {
      if (this.listCard.length === 0) {
        NotifyMessage.leftCornerNotificationError("Chưa có thẻ nào được chọn!");
        return;
      }
      
      if (command === "activeCard") {
        this.activeManyCard();
      } else if (command === "disableCard") {
        this.deActiveManyCard();
      } else if (command === "deleteCards") {
        this.deleteManyCard();
      }
    },

    async findAllCardManager() {
      this.loading.table = true;
      try {
        const response = await cardManagerService.findAllCard(
          this.dataFilter.cardNumber,
          this.dataFilter.usageStatus,
          this.dataFilter.idSchool,
          this.dataFilter.limit,
          this.dataFilter.page
        );
        this.dataTable = response.data.data.cards || [];
        this.totalRecords = response.data.data.totalCards || 0;
      } catch (err) {
        console.error("Failed to load card manager data:", err);
      } finally {
        this.loading.table = false;
      }
    },

    searchInformationCard() {
      this.dataFilter.page = 1;
      this.findAllCardManager();
    },

    async findAllAppIconForSchool() {
      try {
        const resp = await SystemConfigService.findSchoolConfig("", this.dataFilter.idSchool, true);
        if (this.dataFilter.idSchool === "") {
          this.schoolList = resp.data.data || [];
        }
      } catch (err) {
        console.error("Failed to load schools:", err);
      }
    },

    handleCloseDialogAdd() {
      this.isOpen.add = false;
      this.findAllCardManager();
    },

    handleSelectionChange(value) {
      this.listCard = value.map(item => item.id.toString());
    },

    handleCurrentPageChange(value) {
      this.dataFilter.page = value;
      this.findAllCardManager();
    },

    handleSizeChange(value) {
      this.dataFilter.limit = value;
      this.dataFilter.page = 1;
      this.findAllCardManager();
    },

    calculateStt(index) {
      return (this.dataFilter.page - 1) * this.dataFilter.limit + index + 1;
    },

    handleAddCard() {
      this.isOpen.add = true;
    },

    getUsageStatusLabel(status) {
      const labels = {
        use: "Đã sử dụng",
        "not use": "Chưa sử dụng",
        delete: "Hủy kích hoạt"
      };
      return labels[status] || status;
    },

    getUsageStatusTagType(status) {
      const types = {
        use: "success",
        "not use": "primary",
        delete: "danger"
      };
      return types[status] || "info";
    },

    deleteCard(row) {
      this.$confirm("Bạn có chắc chắn muốn xóa thẻ đã chọn?", "Thông báo!", {
        distinguishCancelAndClose: true,
        confirmButtonText: "Có",
        closeOnClickModal: false,
        cancelButtonText: "Không",
        type: "warning"
      }).then(() => {
        cardManagerService.deleteCard(row.id).then((response) => {
          if (response.data.status === 200 && response.data.data === true) {
            NotifyMessage.leftCornerNotificationSuccess("Xóa thẻ thành công!");
          } else {
            NotifyMessage.leftCornerNotificationError("Xóa thẻ thất bại!");
          }
          this.findAllCardManager();
        });
      });
    },

    activeCard(row) {
      this.$confirm("Bạn có chắc chắn muốn kích hoạt thẻ đã chọn?", "Thông báo!", {
        distinguishCancelAndClose: true,
        confirmButtonText: "Có",
        closeOnClickModal: false,
        cancelButtonText: "Không",
        type: "warning"
      }).then(() => {
        cardManagerService.activeCard(row.id).then((response) => {
          if (response.data.status === 200 && response.data.data === true) {
            NotifyMessage.leftCornerNotificationSuccess("Kích hoạt thẻ thành công!");
          } else {
            NotifyMessage.leftCornerNotificationError("Kích hoạt thẻ thất bại!");
          }
          this.findAllCardManager();
        });
      });
    },

    deActiveCard(row) {
      this.$confirm("Bạn có chắc chắn muốn hủy kích hoạt thẻ đã chọn?", "Thông báo!", {
        distinguishCancelAndClose: true,
        confirmButtonText: "Có",
        closeOnClickModal: false,
        cancelButtonText: "Không",
        type: "warning"
      }).then(() => {
        cardManagerService.deActiveCard(row.id).then((response) => {
          if (response.data.status === 200 && response.data.data === true) {
            NotifyMessage.leftCornerNotificationSuccess("Hủy kích hoạt thẻ thành công!");
          } else {
            NotifyMessage.leftCornerNotificationError("Hủy kích hoạt thẻ thất bại!");
          }
          this.findAllCardManager();
        });
      });
    },

    deleteManyCard() {
      this.$confirm("Bạn có chắc chắn muốn xóa các thẻ đã chọn?", "Thông báo!", {
        distinguishCancelAndClose: true,
        confirmButtonText: "Có",
        closeOnClickModal: false,
        cancelButtonText: "Không",
        type: "warning"
      }).then(() => {
        cardManagerService.deleteListCard(this.listCard).then((response) => {
          if (response.data.status === 200 && response.data.data === true) {
            NotifyMessage.leftCornerNotificationSuccess("Xóa nhiều thẻ thành công!");
          } else {
            NotifyMessage.leftCornerNotificationError("Xóa nhiều thẻ thất bại!");
          }
          this.findAllCardManager();
        });
      });
    },

    activeManyCard() {
      this.$confirm("Bạn có chắc chắn muốn kích hoạt các thẻ đã chọn?", "Thông báo!", {
        distinguishCancelAndClose: true,
        confirmButtonText: "Có",
        closeOnClickModal: false,
        cancelButtonText: "Không",
        type: "warning"
      }).then(() => {
        cardManagerService.activeListCard(this.listCard).then((response) => {
          if (response.data.status === 200 && response.data.data === true) {
            NotifyMessage.leftCornerNotificationSuccess("Kích hoạt nhiều thẻ thành công!");
          } else {
            NotifyMessage.leftCornerNotificationError("Kích hoạt nhiều thẻ thất bại!");
          }
          this.findAllCardManager();
        });
      });
    },

    deActiveManyCard() {
      this.$confirm("Bạn có chắc chắn muốn hủy kích hoạt các thẻ đã chọn?", "Thông báo!", {
        distinguishCancelAndClose: true,
        confirmButtonText: "Có",
        closeOnClickModal: false,
        cancelButtonText: "Không",
        type: "warning"
      }).then(() => {
        cardManagerService.deActiveListCard(this.listCard).then((response) => {
          if (response.data.status === 200 && response.data.data === true) {
            NotifyMessage.leftCornerNotificationSuccess("Hủy kích hoạt nhiều thẻ thành công!");
          } else {
            NotifyMessage.leftCornerNotificationError("Hủy kích hoạt nhiều thẻ thất bại!");
          }
          this.findAllCardManager();
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-check-container {
  padding: 16px;
  background-color: #f4f6fa;
  min-height: calc(100vh - 120px);
}

/* Header Banner Styling */
.ai-header-banner {
  position: relative;
  border-radius: 12px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  padding: 24px 32px;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(30, 60, 114, 0.15);

  .banner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  }

  .banner-content {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
    width: 100%;
  }

  .banner-left {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .ai-icon-pulse {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
    animation: iconPulse 3s infinite ease-in-out;

    i {
      font-size: 32px;
      color: #fff;
    }
  }

  .banner-title {
    color: #fff;
    font-size: 22px;
    margin: 0 0 6px 0;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  .banner-subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    margin: 0;
  }

  .banner-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .gradient-btn {
    border: none;
    font-weight: 600;
    transition: all 0.3s;

    &:hover {
      transform: translateY(-2px);
    }

    &.green-btn {
      background: linear-gradient(90deg, #10b981 0%, #059669 100%);
      box-shadow: 0 4px 10px rgba(16, 185, 129, 0.3);
      &:hover { box-shadow: 0 6px 15px rgba(16, 185, 129, 0.4); }
    }

    &.purple-btn {
      background: linear-gradient(90deg, #8b5cf6 0%, #6d28d9 100%);
      box-shadow: 0 4px 10px rgba(139, 92, 246, 0.3);
      &:hover { box-shadow: 0 6px 15px rgba(139, 92, 246, 0.4); }
    }
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 0 25px rgba(255, 255, 255, 0.4);
  }
}

/* Stats Cards Grid */
.ai-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 24px;
  }

  .stat-info {
    flex: 1;
  }

  .stat-label {
    margin: 0 0 4px 0;
    font-size: 13px;
    color: #64748b;
  }

  .stat-value {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
  }

  /* Colors */
  &.blue {
    border-left: 4px solid #3b82f6;
    .stat-icon { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
  }
  &.green {
    border-left: 4px solid #10b981;
    .stat-icon { background: rgba(16, 185, 129, 0.1); color: #10b981; }
  }
  &.purple {
    border-left: 4px solid #8b5cf6;
    .stat-icon { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; }
  }
}

/* Glassmorphic Cards */
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.table-container {
  background: #fff;
  padding: 12px;
}

/* Modern Form Filters */
.modern-filter-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;

  /deep/ .el-form-item {
    margin-bottom: 0 !important;
    margin-right: 0 !important;
  }

  /deep/ .el-form-item__label {
    font-weight: 600;
    color: #475569;
    padding-bottom: 6px;
  }

  .modern-select {
    width: 220px;
  }
  
  .modern-input {
    width: 240px;
  }
}

/* Modern Table Styles */
.modern-table {
  border-radius: 8px;
  overflow: hidden;

  /deep/ th {
    background-color: #f8fafc !important;
    color: #475569;
    font-weight: 600;
    height: 48px;
  }

  /deep/ td {
    padding: 10px 0;
  }
}

/* Cell styles */
.card-number-badge {
  font-family: Consolas, Monaco, monospace;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #cbd5e1;

  i {
    color: #64748b;
  }
}

.school-name-text {
  font-weight: 600;
  color: #3b82f6;
}

.status-tag-rect {
  font-size: 12px;
  height: 24px;
  line-height: 22px;
  padding: 0 10px;
  border-radius: 6px;
  border: none;
}

.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

/* Dropdown styling */
.modern-dropdown-wrapper {
  display: inline-block;
}

.modern-dropdown-menu {
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  border: 1px solid #e2e8f0;
  
  /deep/ .el-dropdown-menu__item {
    font-weight: 600;
    font-size: 13px;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #475569;

    i {
      font-size: 16px;
    }

    &:hover {
      background-color: #f1f5f9;
      color: #3b82f6;
    }
  }
}

.flex {
  display: flex;
}
.gap-2 {
  gap: 8px;
}
</style>